import { useEffect, useState } from "react";
import { useCopy } from "../../../shared/i18n";
import { IconArrowRight } from "../../../shared/console";
import {
  claimInvite,
  previewInvite,
  type ClaimedInvite,
  type InvitePreview,
} from "../../../shared/api/auth";
import type { StaffTokens } from "../../../shared/stores/staffSession";
import { passwordFormFailure, type PasswordFormFailure } from "../signInFlow";
import {
  inviteGreeting,
  passwordProblem,
  passwordRuleItems,
} from "./inviteModel";
import {
  AuthHeading,
  AuthSplit,
  type AuthPoint,
} from "../../console/public/AuthSplit";
import { FormAlert, TextInput } from "../../console/public/fields";
import {
  IconCard,
  IconCheckCircle,
  IconKey,
  IconMail,
} from "../../console/public/icons";
import {
  LockedPasswordInput,
  PasswordChecklist,
  Stepper,
  type StepDef,
} from "../../console/public/SetPasswordParts";
import { LABEL, LINK_QUIET, SUBMIT_FULL } from "../../console/public/styles";

/**
 * Claim a staff invitation and choose a password (OD-100) — Figma P4 "Set your
 * password (first login)", 11:551, in the console look.
 *
 * 🔴 THIS SCREEN IS WHY THE INVITE SYSTEM WORKS AT ALL. `POST /auth/invite` and
 * `/auth/invite/claim` shipped tested and audited, and **nothing in the frontend
 * called either** — so every invitation the product could issue pointed at a
 * screen that did not exist.
 *
 * 🔴 IT IS PUBLIC, AND THE TOKEN IN THE URL IS THE AUTHENTICATION. The invitee
 * has no account they can sign into yet — that is what the invitation is for.
 * The token is 256 bits, single-use and dies in 72 hours, which is what makes
 * putting it in a link acceptable.
 *
 * ✅ P2-02 — IT IS PERSONAL NOW. `POST /auth/invite/preview` answers who the link
 * is for (email, role, school, seats, plan, expiry), so the page greets the
 * invitee the way Figma draws it: "Welcome to Kalqy, Greenwood", a locked email
 * field, "This invite link expires on 15 Oct 2026." The preview answers every
 * dead link with one all-null shape, so the page learns nothing about a token
 * that does not work — and anyone holding one that does could already learn as
 * much by claiming it.
 *
 * ⚠️ ONE MESSAGE FOR EVERY REFUSAL, and this screen must not try to be more
 * helpful than that. Unknown, expired, revoked and already-claimed are one
 * answer server-side, on the preview and on the claim alike.
 */

export interface InviteClaimScreenProps {
  /** Taken from the hash by `inviteTokenFrom`. Never validated in the browser. */
  token: string;
  /**
   * Where "sign in now" goes once the password is set, when no session came back.
   * Given the claimed account's role, so the router can open that role's own door.
   */
  onDone?: (claimed: ClaimedInvite | null) => void;
  /**
   * ✅ P2-02 — the session `claim` hands back when asked (never for an operator,
   * whose second factor comes first). The router stores it SESSION-ONLY and takes
   * the person straight into their tier — a new school admin lands on Pay rather
   * than typing the password they just chose into a sign-in form.
   */
  onSignedIn?: (tokens: StaffTokens) => void;
}

export function InviteClaimScreen({
  token,
  onDone,
  onSignedIn,
}: InviteClaimScreenProps) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [state, setState] = useState<"idle" | "saving" | "done">("idle");
  const [failure, setFailure] = useState<PasswordFormFailure | null>(null);
  /* Whose account this was — only known once the claim has succeeded. */
  const [claimed, setClaimed] = useState<ClaimedInvite | null>(null);
  /**
   * Who the link is for. `null` while loading AND when the preview could not be
   * read — the page then says what is true of any invitee, as it always did.
   */
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [previewLoaded, setPreviewLoaded] = useState(false);

  useEffect(() => {
    let live = true;
    previewInvite(token)
      .then((p) => {
        if (live) setPreview(p);
      })
      .catch(() => {
        /* A preview that fails is a generic page, never a refusal: the claim
           itself is still the authority on whether this link works. */
        if (live) setPreview(null);
      })
      .finally(() => {
        if (live) setPreviewLoaded(true);
      });
    return () => {
      live = false;
    };
  }, [token]);

  const greeting = inviteGreeting(preview);
  /* A preview that came back `valid: false` is the one generic refusal, up front. */
  const deadLink = preview !== null && !preview.valid;
  const schoolAdmin = greeting.journey === "school-admin";

  const t = useCopy({
    en: {
      title: "Set your password",
      email: "Email",
      emailLocked:
        "This is the address Kalqy invited. It cannot be changed here.",
      stepApproved: "Approved by Kalqy",
      stepInvited: "Invited",
      stepInvitedCaption: "Link opened",
      stepSet: "Set password",
      stepPay: "Pay",
      stepSignIn: "Sign in",
      now: "Now",
      next: "Next",
      done: "Done",
      password: "New password",
      confirm: "Confirm password",
      confirmPlaceholder: "Type it again",
      submit: "Set password & continue",
      saving: "Saving…",
      mismatch: "Those two do not match.",
      rule: "That password does not meet the rules above. Choose another.",
      /*
       * ⚠️ ONE SENTENCE FOR FOUR REFUSALS, and it names the fix rather than the
       * cause — because the product genuinely does not know which of the four it
       * was, and the action is the same for all of them.
       */
      refused:
        "This invitation is no longer valid. Ask whoever invited you to send a new one.",
      throttled: "Too many attempts from here. Wait a minute and try again.",
      failed: "Couldn’t reach Kalqy. Check the connection and try again.",
      doneTitle: "Your password is set",
      doneBody:
        "Sign in with your email and the password you just chose. Your role was decided when the account was created — there is nothing to choose.",
      doneBodyAs: (email: string) =>
        `Sign in as ${email} with the password you just chose. Your role was decided when the account was created — there is nothing to choose.`,
      signIn: "Sign in",
      schoolAdminLink: "School admin? Use the school admin sign-in",
      pointSetTitle: "Set a password",
      pointSetBody: "Only you can see it. Kalqy never asks for it.",
      pointPayTitle: "Pay, then add children",
      pointPayBody: "Try the games while your invoice is open.",
      pointSignInTitle: "Then sign in",
      pointSignInBody: "Use your email and this password on the sign-in page.",
    },
  });

  const points: readonly AuthPoint[] = [
    {
      icon: IconCheckCircle,
      title: greeting.approvedTitle,
      body: greeting.approvedBody,
    },
    { icon: IconKey, title: t.pointSetTitle, body: t.pointSetBody },
    schoolAdmin
      ? { icon: IconCard, title: t.pointPayTitle, body: t.pointPayBody }
      : {
          icon: IconArrowRight,
          title: t.pointSignInTitle,
          body: t.pointSignInBody,
        },
  ];

  const problem = passwordProblem(password, confirm);
  const busy = state === "saving";

  async function submit(): Promise<void> {
    if (problem !== null || busy) return;
    setState("saving");
    setFailure(null);
    try {
      const result = await claimInvite(token, password, { signIn: true });
      setPassword("");
      setConfirm("");
      /*
       * ✅ SIGNED IN ON CLAIM WHEN THE SERVER SAYS SO. The router stores the pair
       * session-only and replaces this screen; a role whose second factor comes
       * first (an operator) gets no session and the "Sign in" step below.
       */
      if (result.session && onSignedIn) {
        onSignedIn(result.session);
        return;
      }
      setClaimed(result);
      setState("done");
    } catch (err) {
      /*
       * ⚠️ 0 IS "NO ANSWER AT ALL" — offline, DNS, timeout. Separated from a
       * refusal because somebody on a dropped connection who reads "this
       * invitation is no longer valid" will go and ask for a new link they did
       * not need.
       */
      setFailure(
        passwordFormFailure((err as { status?: number } | null)?.status),
      );
      setState("idle");
    }
  }

  const done = state === "done";
  const steps: readonly StepDef[] = schoolAdmin
    ? [
        { title: t.stepApproved, caption: t.done, state: "done" },
        {
          title: t.stepSet,
          caption: done ? t.done : t.now,
          state: done ? "done" : "current",
        },
        { title: t.stepPay, caption: t.next, state: "next" },
      ]
    : [
        { title: t.stepInvited, caption: t.stepInvitedCaption, state: "done" },
        {
          title: t.stepSet,
          caption: done ? t.done : t.now,
          state: done ? "done" : "current",
        },
        {
          title: t.stepSignIn,
          caption: done ? t.now : t.next,
          state: done ? "current" : "next",
        },
      ];

  if (done) {
    return (
      <AuthSplit
        headline={greeting.headline}
        points={points}
        testId="invite-claim"
        width="wide"
      >
        <Stepper steps={steps} />
        <div className="mt-7">
          <AuthHeading
            title={t.doneTitle}
            subtitle={claimed?.email ? t.doneBodyAs(claimed.email) : t.doneBody}
          />
        </div>
        <button
          type="button"
          onClick={() => {
            onDone?.(claimed);
          }}
          className={`${SUBMIT_FULL} mt-6`}
        >
          {t.signIn}
          <IconArrowRight size={16} />
        </button>
        {/* Only a fallback now: with the role known, the button is already the right door. */}
        {claimed?.role ? null : (
          <p className="mt-4 text-center">
            <a href="#/school/signin" className={LINK_QUIET}>
              {t.schoolAdminLink}
            </a>
          </p>
        )}
      </AuthSplit>
    );
  }

  const showMismatch = confirm.length > 0 && problem === "MISMATCH";
  const lockedEmail = preview?.valid ? preview.email : null;

  return (
    <AuthSplit
      headline={greeting.headline}
      points={points}
      testId="invite-claim"
      width="wide"
    >
      <Stepper steps={steps} />
      <form
        className="mt-7"
        noValidate
        aria-busy={!previewLoaded || undefined}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <AuthHeading title={t.title} subtitle={greeting.invitedLine} />

        {deadLink ? (
          <div className="mt-6">
            <FormAlert testId="invite-refused">{t.refused}</FormAlert>
          </div>
        ) : (
          <>
            {lockedEmail ? (
              <>
                <label className={`${LABEL} mt-6`} htmlFor="invite-email">
                  {t.email}
                </label>
                <div className="mt-2">
                  {/* 🔴 Read-only: the address is the account's, chosen by whoever invited them. */}
                  <TextInput
                    id="invite-email"
                    type="email"
                    icon={IconMail}
                    value={lockedEmail}
                    readOnly
                    className="bg-surface-muted text-ink-soft"
                    aria-readonly="true"
                    aria-describedby="invite-email-locked"
                    autoComplete="username"
                  />
                </div>
                <p id="invite-email-locked" className="sr-only">
                  {t.emailLocked}
                </p>
              </>
            ) : null}

            <label className={`${LABEL} mt-5`} htmlFor="invite-password">
              {t.password}
            </label>
            <div className="mt-2">
              <LockedPasswordInput
                id="invite-password"
                autoComplete="new-password"
                maxLength={200}
                value={password}
                aria-describedby="invite-rules"
                onChange={(e) => {
                  setPassword(e.target.value);
                }}
              />
            </div>
            <PasswordChecklist
              id="invite-rules"
              items={passwordRuleItems(password)}
            />

            <label
              className={`${LABEL} mt-5`}
              htmlFor="invite-password-confirm"
            >
              {t.confirm}
            </label>
            <div className="mt-2">
              <LockedPasswordInput
                id="invite-password-confirm"
                autoComplete="new-password"
                maxLength={200}
                placeholder={t.confirmPlaceholder}
                value={confirm}
                aria-invalid={showMismatch || undefined}
                aria-describedby={showMismatch ? "invite-mismatch" : undefined}
                onChange={(e) => {
                  setConfirm(e.target.value);
                }}
              />
            </div>
            {/* 🔴 Shown only once the second box has been typed into, so the form
                does not shout at somebody mid-word. */}
            {showMismatch ? (
              <p
                id="invite-mismatch"
                className="mt-1.5 text-[12px] leading-4 text-danger"
              >
                {t.mismatch}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={busy || problem !== null}
              aria-busy={busy || undefined}
              className={`${SUBMIT_FULL} mt-6`}
            >
              {busy ? null : <IconArrowRight size={16} />}
              {busy ? t.saving : t.submit}
            </button>

            {failure !== null && (
              <div className="mt-4">
                <FormAlert
                  testId={
                    failure === "refused" ? "invite-refused" : "invite-failed"
                  }
                >
                  {failure === "rule"
                    ? t.rule
                    : failure === "refused"
                      ? t.refused
                      : failure === "throttled"
                        ? t.throttled
                        : t.failed}
                </FormAlert>
              </div>
            )}
          </>
        )}

        <p className="mt-6 text-[12px] leading-4 text-muted">
          {greeting.expiryLine}
        </p>
      </form>
    </AuthSplit>
  );
}
